import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import Button from '../../components/Button/Button';
import { ArrowLeftIcon } from '../../components/Icon/Icon';
import Loader from '../../components/Loader/Loader';
import Price from '../../components/Price/Price';
import { useCommon } from '../../context/CommonContext';
import { useLocale } from '../../context/LocaleContext';
import { RoutesPathE } from '../../declarations/enums/routes';
import useReactRouter from '../../hooks/useReactRouter';
import CertificatePopup from './components/CertificatePopup';
import {
  CardImageWrapperStyled,
  CardNameStyled,
  CardsWrapperStyled,
  CardWrapperStyled,
  CertificateButtonStyled,
  CertificateButtonsWrapperStyled,
  ColCommonDetailsStyled,
  ColStyled,
  ColSummaryStyled,
  ConfirmationPageStyled,
  ContentStyled,
  ContentTitleStyled,
  DetailsListStyled,
  DetailsWrapperStyled,
  IdStyled,
  ListStyled,
  LogoWrapperStyled,
  NoteStyled,
  SectionTitleStyled,
  SubTitleStyled,
  SummaryBottomStyled,
  SummaryContentStyled,
  SummaryLabelStyled,
  SummaryRowStyled,
  SummaryTopStyled,
  SummaryWrapperStyled,
  TopSectionStyled,
} from './ConfirmationPage.styled';
import useConfirmation from './hooks/useConfirmation';

const ConfirmationPage: React.FC = () => {
  const { navigateTo } = useReactRouter();
  const { confirmationId = '' } = useParams();
  const { logoUrl } = useCommon();
  const { selectedLocale } = useLocale();
  const {
    isLoading,
    billingDetails,
    selectedItems,
    certificateUrl,
    socialTitle,
    popupTitle,
    confirmationNumber,
    handleShowCertificate,
  } = useConfirmation(confirmationId);
  const [isCertificateVisible, setIsCertificateVisible] = useState(false);

  const { address, firstName, lastName, last4Digits, phoneNo, email, cardBrand, purchaseDate } =
    billingDetails;
  const { carbonOffsetProjects, sustainableActionsForConfirmationPage, totalCost } = selectedItems;
  const hasSustainableActions =
    !!sustainableActionsForConfirmationPage && !!sustainableActionsForConfirmationPage.length;
  const hasCarbonProjects = !!carbonOffsetProjects && !!carbonOffsetProjects.length;

  const handleOnClickCertificate = (type: 'cop' | 'sa') => {
    setIsCertificateVisible(true);
    handleShowCertificate(type);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <ConfirmationPageStyled>
      <TopSectionStyled>
        <Button
          modifier="inline"
          isUpperCase={false}
          icon={<ArrowLeftIcon />}
          onClick={() => navigateTo(RoutesPathE.HOME_PAGE)}
        >
          <FormattedMessage id="button.backToHome" defaultMessage="Back to home" />
        </Button>
        <LogoWrapperStyled>
          <img src={logoUrl} alt="logo" />
        </LogoWrapperStyled>
      </TopSectionStyled>

      <IdStyled>
        <FormattedMessage id="confirmation.confirmationId" defaultMessage="Confirmation ID" />:
        {confirmationNumber}
      </IdStyled>

      <DetailsWrapperStyled>
        <ColStyled>
          <SectionTitleStyled>
            <FormattedMessage id="confirmation.yourDetails" defaultMessage="Your Details" />
          </SectionTitleStyled>

          <SubTitleStyled>
            <FormattedMessage id="confirmation.billedTo" defaultMessage="Billed To" />:
          </SubTitleStyled>

          <ListStyled>
            <li>
              <FormattedMessage id="confirmation.name" defaultMessage="Name" />:{' '}
              <strong>
                {firstName} {lastName}
              </strong>
            </li>
            <li>
              <FormattedMessage id="form.address" defaultMessage="Address" />:{' '}
              <strong>{address}</strong>
            </li>
            <li>
              <FormattedMessage id="confirmation.phoneNo" defaultMessage="Phone No" />:{' '}
              <strong>{phoneNo}</strong>
            </li>
            <li>
              <FormattedMessage id="confirmation.email" defaultMessage="Email" />:{' '}
              <strong>{email}</strong>
            </li>
          </ListStyled>

          <SubTitleStyled>
            <FormattedMessage id="confirmation.paymentMethod" defaultMessage="Payment method" />:
          </SubTitleStyled>

          <ListStyled>
            <li>
              <FormattedMessage id="confirmation.cardType" defaultMessage="Card type" />:{' '}
              <strong>{cardBrand}</strong>
            </li>
            <li>
              <FormattedMessage id="confirmation.creditCardNo" defaultMessage="Credit Card No" />:{' '}
              <strong>xxxx xxxx xxxx {last4Digits}</strong>
            </li>
          </ListStyled>

          <SubTitleStyled>
            <FormattedMessage id="confirmation.purchaseDate" defaultMessage="Purchase date" />:
          </SubTitleStyled>

          <ListStyled>
            <li>
              <strong>{purchaseDate[selectedLocale]}</strong>
            </li>
          </ListStyled>
        </ColStyled>
        <ColStyled>
          {hasCarbonProjects && (
            <>
              <SectionTitleStyled>
                <FormattedMessage
                  id="offset.project.title"
                  defaultMessage="Carbon Offset Project"
                />
              </SectionTitleStyled>

              <CardsWrapperStyled>
                {carbonOffsetProjects.map(
                  ({ imageUrl, name, pricePerUnit, total, quantity }, index) => (
                    <CardWrapperStyled key={index}>
                      <CardNameStyled>{name[selectedLocale]}</CardNameStyled>
                      <CardImageWrapperStyled>
                        <img src={imageUrl} alt="project-card" />
                      </CardImageWrapperStyled>

                      <DetailsListStyled>
                        <li>
                          <FormattedMessage
                            id="foundItem.priceTonne"
                            defaultMessage="Price/tonne"
                          />
                          : <Price amount={pricePerUnit} />
                        </li>
                        <li>
                          <FormattedMessage
                            id="foundItem.numberOfTonnes"
                            defaultMessage="Number of tonnes"
                          />
                          : <strong>{quantity}</strong>
                        </li>
                        <li>
                          <FormattedMessage
                            id="foundItem.totalCharge"
                            defaultMessage="Total Charge"
                          />
                          : <Price amount={total} />
                        </li>
                      </DetailsListStyled>
                    </CardWrapperStyled>
                  )
                )}
              </CardsWrapperStyled>
            </>
          )}

          {hasSustainableActions && (
            <>
              <SectionTitleStyled>
                <FormattedMessage
                  id="sustainableActions.title"
                  defaultMessage="Sustainable Actions"
                />
              </SectionTitleStyled>

              <CardsWrapperStyled>
                {sustainableActionsForConfirmationPage.map(
                  ({ imageUrl, name, cost, pricePerUnit, country, type }, index) => (
                    <CardWrapperStyled key={index}>
                      <CardNameStyled>{name[selectedLocale]}</CardNameStyled>
                      <CardImageWrapperStyled>
                        <img src={imageUrl} alt="sa-card" />
                      </CardImageWrapperStyled>

                      <DetailsListStyled>
                        <li>
                          <FormattedMessage id="foundItem.cost" defaultMessage="Unit Cost" />
                          : <Price amount={pricePerUnit} />
                        </li>
                        <li>
                          <FormattedMessage id="foundItem.type" defaultMessage="Type" />:{' '}
                          <strong>{type[selectedLocale]}</strong>
                        </li>
                        <li>
                          {' '}
                          <FormattedMessage id="foundItem.country" defaultMessage="Country" />:{' '}
                          <strong>{country[selectedLocale]}</strong>
                        </li>
                        <li>
                          <FormattedMessage
                            id="foundItem.totalCharge"
                            defaultMessage="Total Charge"
                          />
                          : <Price amount={cost} />
                        </li>
                      </DetailsListStyled>
                    </CardWrapperStyled>
                  )
                )}
              </CardsWrapperStyled>
            </>
          )}
        </ColStyled>
      </DetailsWrapperStyled>

      <SummaryWrapperStyled>
        <SummaryTopStyled>
          <ColSummaryStyled>
            <SummaryLabelStyled level={4} isUppercase={false}>
              <FormattedMessage id="common.summary" defaultMessage="Summary" />
            </SummaryLabelStyled>
          </ColSummaryStyled>

          <ColCommonDetailsStyled>
            <SummaryLabelStyled level={4} isUppercase={false}>
              <FormattedMessage id="common.price" defaultMessage="Price" />
            </SummaryLabelStyled>
          </ColCommonDetailsStyled>

          <ColCommonDetailsStyled>
            <SummaryLabelStyled level={4} isUppercase={false}>
              <FormattedMessage id="common.qty" defaultMessage="Qty" />
            </SummaryLabelStyled>
          </ColCommonDetailsStyled>

          <ColCommonDetailsStyled>
            <SummaryLabelStyled level={4} isUppercase={false}>
              <FormattedMessage id="common.total" defaultMessage="Total" />
            </SummaryLabelStyled>
          </ColCommonDetailsStyled>
        </SummaryTopStyled>

        <SummaryContentStyled>
          {hasCarbonProjects && (
            <ContentStyled>
              <ContentTitleStyled>
                <FormattedMessage
                  id="offset.project.title"
                  defaultMessage="Carbon Offset Project"
                />
              </ContentTitleStyled>

              {carbonOffsetProjects.map(({ name, pricePerUnit, quantity, total }, index) => (
                <SummaryRowStyled key={index}>
                  <ColSummaryStyled>{name[selectedLocale]}</ColSummaryStyled>
                  <ColCommonDetailsStyled>
                    <Price amount={pricePerUnit} />
                  </ColCommonDetailsStyled>
                  <ColCommonDetailsStyled>
                    <strong>{quantity}</strong>
                  </ColCommonDetailsStyled>
                  <ColCommonDetailsStyled>
                    <Price amount={total} />
                  </ColCommonDetailsStyled>
                </SummaryRowStyled>
              ))}
            </ContentStyled>
          )}

          {hasSustainableActions && (
            <ContentStyled>
              <ContentTitleStyled>
                <FormattedMessage
                  id="sustainableActions.title"
                  defaultMessage="Sustainable Actions"
                />
              </ContentTitleStyled>

              {sustainableActionsForConfirmationPage.map(
                ({ pricePerUnit, name, quantity, cost }, index) => (
                  <SummaryRowStyled key={index}>
                    <ColSummaryStyled>{name[selectedLocale]}</ColSummaryStyled>
                    <ColCommonDetailsStyled>
                      <Price amount={pricePerUnit} />
                    </ColCommonDetailsStyled>
                    <ColCommonDetailsStyled>
                      <strong>{quantity}</strong>
                    </ColCommonDetailsStyled>
                    <ColCommonDetailsStyled>
                      <Price amount={cost} />
                    </ColCommonDetailsStyled>
                  </SummaryRowStyled>
                )
              )}
            </ContentStyled>
          )}
        </SummaryContentStyled>

        <SummaryBottomStyled>
          <FormattedMessage id="common.totalPaid" defaultMessage="Total Paid" />
          :&nbsp; <Price amount={totalCost} />
        </SummaryBottomStyled>
      </SummaryWrapperStyled>

      <CertificateButtonsWrapperStyled>
        {hasCarbonProjects && (
          <>
            <CertificateButtonStyled
              isUpperCase={false}
              modifier="inline"
              onClick={() => handleOnClickCertificate('cop')}
            >
              <FormattedMessage
                id="confirmation.downloadOffsetCertificate"
                defaultMessage="Download your Carbon Offset Certificate"
              />
            </CertificateButtonStyled>
          </>
        )}

        {hasSustainableActions && (
          <CertificateButtonStyled
            isUpperCase={false}
            modifier="inline"
            onClick={() => handleOnClickCertificate('sa')}
          >
            <FormattedMessage
              id="confirmation.downloadActionsCertificate"
              defaultMessage="Download your Sustainable Actions Certificate"
            />
          </CertificateButtonStyled>
        )}
      </CertificateButtonsWrapperStyled>

      <NoteStyled>
        <FormattedMessage
          id="confirmation.note"
          defaultMessage="* A pdf copy of this certificate will be attached to your confirmation email."
        />
      </NoteStyled>

      <CertificatePopup
        popupTitle={popupTitle}
        certificateURL={certificateUrl}
        socialTitle={socialTitle}
        isOpen={isCertificateVisible}
        onClose={() => setIsCertificateVisible(false)}
      />
    </ConfirmationPageStyled>
  );
};

export default ConfirmationPage;
